import React, { useState, useEffect } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Modal, Spin, Upload, message, Badge, Tag, Select, Card, Statistic } from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined, StarOutlined, UploadOutlined, TeamOutlined, PictureOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGraduate, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import PasswordModal from './PasswordModal';
import EditStudentModal from './EditStudentModal';
import EditLinkedStudentForm from './EditLinkedStudentForm';
import StudentBookImagesModal from './StudentBookImagesModal';

// Accent colours cycled across the sales cards
const SALES_CARD_COLORS = [
  '#52c41a',
  '#722ed1',
  '#fa8c16',
  '#13c2c2',
  '#eb2f96',
  '#2f54eb',
  '#faad14',
  '#f5222d',
];
const TOTAL_CARD_COLOR = '#1677ff';
const NO_SALES_CARD_COLOR = '#8c8c8c';

function EditDeleteStudent({ onCreateNewStudent }) {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [studentIdToDelete, setStudentIdToDelete] = useState(null);
  const [studentIdToEdit, setStudentIdToEdit] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [isLinkedStudentModalOpen, setIsLinkedStudentModalOpen] = useState(false);
  const [isBookImagesModalOpen, setIsBookImagesModalOpen] = useState(false);
  const [bookImagesStudent, setBookImagesStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageFilter, setAgeFilter] = useState([]);
  const [levelFilter, setLevelFilter] = useState([]);
  const [schoolFilter, setSchoolFilter] = useState([]);
  const [ageOptions, setAgeOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [salesFilter, setSalesFilter] = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);
  const [salesStats, setSalesStats] = useState([]);
  const rowsPerPage = 100;
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;



const fetchStudents = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');

    // The search term and the dropdown filters are all applied by the
    // backend, so they match across every student, not just this page
    const response = await axios.get(`${API_URL}/students`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        page: currentPage,
        size: rowsPerPage,
        search,
        age: ageFilter,
        level: levelFilter,
        schoolName: schoolFilter,
        salesperson: salesFilter,
        sortBy: 'schoolid',
        sortOrder: 'desc'
      },
      // repeat keys instead of age[]= so the backend reads them as arrays
      paramsSerializer: { indexes: null },
    });


    setStudents(response.data.students); 
    setTotalItems(response.data.totalItems);
    setTotalPages(response.data.totalPages);
  } catch (error) {
    console.error('Error fetching students:', error);
    setError(t('fetchStudentsError'));
  } finally {
    setLoading(false);
  }
};
useEffect(() => {
  fetchStudents();
}, [currentPage, search, ageFilter, levelFilter, schoolFilter, salesFilter]);

// Dropdown options and the sales counts come from the whole student table,
// not just the loaded page
const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/students/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const toOptions = (values) =>
        (values || []).map((value) => ({ value: String(value), label: String(value) }));

      setAgeOptions(toOptions(response.data.ages));
      setLevelOptions(toOptions(response.data.levels));
      setSchoolOptions(toOptions(response.data.schools));

      // A null id is the "no salesperson assigned" bucket
      const salesRows = (response.data.salespersons || []).map((person) => ({
        key: person.id === null ? 'none' : String(person.id),
        label:
          person.id === null
            ? t('No Sales')
            : person.nickname || person.full_name || String(person.id),
        count: person.student_count || 0,
      }));

      setSalesOptions(
        salesRows
          .filter((row) => row.key !== 'none')
          .map((row) => ({ value: row.key, label: row.label }))
          .concat({ value: 'none', label: t('No Sales') })
      );

      // Biggest first, so the cards read as a ranking
      setSalesStats([...salesRows].sort((a, b) => b.count - a.count));
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
};

useEffect(() => {
  fetchFilterOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [API_URL]);


const refreshStudentData = () => {
  fetchStudents();
  fetchFilterOptions(); // keep the sales counts in step with the table
};

const handleSearch = () => {
  setSearch(searchTerm.trim());
  setCurrentPage(1);
};

const handleFilterChange = (setFilter) => (value) => {
  setFilter(value || []);
  setCurrentPage(1);
};

// Clicking a card adds or removes that salesperson from the filter
const toggleSalesFilter = (key) => {
  setSalesFilter((prev) =>
    prev.includes(key) ? prev.filter((value) => value !== key) : [...prev, key]
  );
  setCurrentPage(1);
};

const totalStudentCount = salesStats.reduce((sum, row) => sum + row.count, 0);

const handleClearSearch = () => {
  setSearchTerm('');
  setSearch('');
  setAgeFilter([]);
  setLevelFilter([]);
  setSchoolFilter([]);
  setSalesFilter([]);
  setCurrentPage(1);
};




  const handleEdit = (studentId) => {
    setStudentIdToEdit(studentId);
    setIsEditModalOpen(true);
  };

  const handleOpenBookImages = (student) => {
    setBookImagesStudent(student);
    setIsBookImagesModalOpen(true);
  };

  const handleEditLinkedStudent = (studentId) => {
  setStudentIdToEdit(studentId); // Set the main student ID
  setIsLinkedStudentModalOpen(true); // Open the linked students modal
};


  const handleDelete = (studentId) => {
    setStudentIdToDelete(studentId);
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const confirmDelete = async (password) => {
    setIsPasswordModalOpen(false);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('noTokenFound'));
        return;
      }
      await axios.delete(`${API_URL}/students/${studentIdToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { password },
      });
      setStudents(prev => prev.filter(student => student.userid !== studentIdToDelete));
      fetchFilterOptions();

      Modal.success({ title: t('success'), content: t('studentDeleted') });
    } catch (error) {
      console.error('Error deleting student:', error);
      if (error.response && error.response.status === 401) {
        setPasswordError(t('invalidPassword'));
        setIsPasswordModalOpen(true);
      } else {
        setError(t('deleteStudentError'));
      }
    }
  };

  const saveStudent = async (updatedStudent) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/students/${updatedStudent.userid}`, updatedStudent, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(prev => prev.map(student => (student.userid === updatedStudent.userid ? updatedStudent : student)));
      setIsEditModalOpen(false);
      fetchFilterOptions();

      Modal.success({ title: t('success'), content: t('studentUpdated') });
    } catch (error) {
      console.error('Error updating student:', error);
      setError(t('updateStudentError'));
    }
  };


  const handleNationalIdUpload = async (studentId) => {
    // Create a modal for file upload
    let fileToUpload = null;
  
    Modal.confirm({
      title: t('Upload National ID'),
      content: (
        <Upload
          beforeUpload={(file) => {
            // Only accept PDF, JPG, JPEG, and PNG files
            const isValidType = file.type === 'application/pdf' || 
                                file.type === 'image/jpeg' || 
                                file.type === 'image/jpg' || 
                                file.type === 'image/png';
            if (!isValidType) {
              message.error(t('onlyPdfAndImagesAllowed'));
              return Upload.LIST_IGNORE;
            }
            
            // Store the file for later use
            fileToUpload = file;
            return false; // Prevent auto upload
          }}
          maxCount={1}
          accept=".pdf,.jpg,.jpeg,.png"
        >
          <Button icon={<UploadOutlined />}>{t('Select File')}</Button>
        </Upload>
      ),
      onOk: async () => {
        if (!fileToUpload) {
          message.error(t('Please Select File'));
          return;
        }
  
        try {
          const token = localStorage.getItem('token');
          const formData = new FormData();
          formData.append('nationalIdFile', fileToUpload); // Make sure this field name matches
          formData.append('userId', studentId);
      
          // Fix the endpoint URL
          await axios.post(`${API_URL}/upload/national_ids/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          });
  
          message.success(t('National ID Uploaded Successfully'));
          
          // Refresh the student list, keeping the active search and filters
          await fetchStudents();
        } catch (error) {
          console.error('Error uploading national ID:', error);
          message.error(t('Upload Failed.'));
        }
      },
      onCancel: () => {}
    });
  };
  
  const nationalIdColumn = {
    title: t('nationalId'),
    dataIndex: 'national_id_pdf',
    render: (pdf, student) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {pdf ? (
          <>
            <a 
              href={`${API_URL}/upload/national_ids/${pdf.split('/').pop()}`} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ marginRight: '8px' }}
            >
              <FontAwesomeIcon icon={faFilePdf} style={{ color: 'red' }} />
            </a>
            
            {/* Option to replace the file */}
            <Button 
              size="small" 
              type="text" 
              icon={<UploadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleNationalIdUpload(student.userid);
              }}
              title={t('replaceFile')}
            />
          </>
        ) : (
          <Button 
            size="small" 
            icon={<UploadOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              handleNationalIdUpload(student.userid);
            }}
          >
          </Button>
        )}
      </div>
    ),
  };

  const viewStudentDashboard = (studentId) => {
    navigate(`/student-dashboard-admin/${studentId}`);
  };

  const viewStudentPerformance = (studentId) => {
    navigate(`/studentperformance/${studentId}`);
  };

  const columns = [
    {
      title: '',
      dataIndex: 'dashboard',
      render: (_, student) => (
        <Button shape="circle"
                      icon={<UserOutlined />} onClick={() => viewStudentDashboard(student.userid)} />
      ),
    },
    {
      title: '',
      dataIndex: 'performance',
      render: (_, student) => (
        <Button shape="circle"
        icon={<StarOutlined />} onClick={() => viewStudentPerformance(student.userid)} />
      ),
    },
    { 
      title: t('id'), 
      dataIndex: 'schoolid'
    },
    { title: t('name'), dataIndex: 'name', render: (_, student) => `${student.firstname} ${student.lastname}` },
    { title: t('nickname'), dataIndex: 'nickname' },
    { title: t('age'), dataIndex: 'age' },
 {
  title: t('level'),
  dataIndex: 'currenteducationlevel',
  render: (level) => {
    if (!level) return '-'; // Handle null/undefined values
    return level; // Just return the level as-is
  }
},
    {
      title: t('school'),
      dataIndex: 'schoolname',
      width: 220,
      // long school names wrap instead of stretching the column
      render: (school) => (
        <div style={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {school || '-'}
        </div>
      ),
    },
 {
  title: t('Sales'),
  dataIndex: 'salesperson_nickname',
  key: 'salesperson_nickname',
  render: (nickname) =>
    nickname ? (
      <Tag color="blue">{nickname}</Tag>
    ) : (
      <Tag color="default">{t('No Sales')}</Tag>
    ),
},

    nationalIdColumn,
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const isActive = status?.toLowerCase() === 'active';
        return (
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? t('Active') : t('Inactive')}
          </Tag>
        );
      }
    },
    {
      title: t('created at'),
      dataIndex: 'createdat',
      key: 'createdat',
      render: (date) => {
        if (!date) return '-';
        return new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'short',
          year: '2-digit',
        }).format(new Date(date));
      }
    },
     {
          title: t('actions'),
          dataIndex: 'actions',
          render: (_, student) => (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => handleEdit(student.userid)}
              />
              <Button
                shape="circle"
                type="primary"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(student.userid)}
              />
              <Badge
        count={student.linked_students?.length || 0}
        showZero={false}
        style={{ backgroundColor: '#52c41a' }}
      >
        <Button
          shape="circle"
          type="default"
          icon={<TeamOutlined />}
          title={t('editLinkedStudent')}
          onClick={() => handleEditLinkedStudent(student.userid)}
        />
      </Badge>
              <Badge
                count={student.book_images_count || 0}
                showZero={false}
                style={{ backgroundColor: '#1890ff' }}
              >
                <Button
                  shape="circle"
                  type="default"
                  icon={<PictureOutlined />}
                  title={t('bookImages')}
                  onClick={() => handleOpenBookImages(student)}
                />
              </Badge>
            </div>
          ),
        }
  ];

  if (loading) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
      <Spin tip={t('loading')} size="large" />
    </div>
  );
}

  if (error) return <p>{error}</p>;

  return (
    <div>
      <Button type="primary" icon={<FontAwesomeIcon icon={faUserGraduate} />} onClick={onCreateNewStudent}>
        {t('createNewStudent')}
      </Button>
      {salesStats.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            marginTop: 16,
          }}
        >
          <Card
            size="small"
            styles={{ body: { padding: '10px 14px' } }}
            style={{
              // grow to share the row evenly, however many cards there are
              flex: '1 1 150px',
              minWidth: 150,
              borderTop: `3px solid ${TOTAL_CARD_COLOR}`,
            }}
          >
            <Statistic
              title={t('All Students')}
              value={totalStudentCount}
              valueStyle={{ color: TOTAL_CARD_COLOR, fontWeight: 600 }}
            />
          </Card>
          {salesStats.map((row, index) => {
            const isActive = salesFilter.includes(row.key);
            const color =
              row.key === 'none'
                ? NO_SALES_CARD_COLOR
                : SALES_CARD_COLORS[index % SALES_CARD_COLORS.length];

            return (
              <Card
                key={row.key}
                size="small"
                hoverable
                onClick={() => toggleSalesFilter(row.key)}
                styles={{ body: { padding: '10px 14px' } }}
                style={{
                  flex: '1 1 150px',
                  minWidth: 150,
                  borderTop: `3px solid ${color}`,
                  borderColor: isActive ? color : undefined,
                  background: isActive ? `${color}1A` : undefined,
                }}
              >
                <Statistic
                  title={
                    <span
                      title={row.label}
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.label}
                    </span>
                  }
                  value={row.count}
                  valueStyle={{ color, fontWeight: 600 }}
                />
              </Card>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 16, marginBottom: 16 }}>
  <Input.Search
    placeholder={t('Search by name, nickname, phone or ID')}
    value={searchTerm}
    onChange={(e) => {
      setSearchTerm(e.target.value);
      // clicking the clear icon should immediately reload the full list
      if (!e.target.value) {
        setSearch('');
        setCurrentPage(1);
      }
    }}
    onSearch={handleSearch}
    allowClear
    onPressEnter={handleSearch}
    style={{ marginBottom: 8, width: 360, marginRight: 16 }}
  />
  <Select
    mode="multiple"
    showSearch
    allowClear
    maxTagCount="responsive"
    placeholder={t('age')}
    value={ageFilter}
    onChange={handleFilterChange(setAgeFilter)}
    options={ageOptions}
    optionFilterProp="label"
    style={{ marginBottom: 8, minWidth: 140, maxWidth: 260, marginRight: 16 }}
  />
  <Select
    mode="multiple"
    showSearch
    allowClear
    maxTagCount="responsive"
    placeholder={t('level')}
    value={levelFilter}
    onChange={handleFilterChange(setLevelFilter)}
    options={levelOptions}
    optionFilterProp="label"
    style={{ marginBottom: 8, minWidth: 200, maxWidth: 320, marginRight: 16 }}
  />
  <Select
    mode="multiple"
    showSearch
    allowClear
    maxTagCount="responsive"
    placeholder={t('school')}
    value={schoolFilter}
    onChange={handleFilterChange(setSchoolFilter)}
    options={schoolOptions}
    optionFilterProp="label"
    style={{ marginBottom: 8, minWidth: 240, maxWidth: 360, marginRight: 16 }}
  />
  <Select
    mode="multiple"
    showSearch
    allowClear
    maxTagCount="responsive"
    placeholder={t('Sales')}
    value={salesFilter}
    onChange={handleFilterChange(setSalesFilter)}
    options={salesOptions}
    optionFilterProp="label"
    style={{ marginBottom: 8, minWidth: 180, maxWidth: 300, marginRight: 16 }}
  />
  <Button onClick={handleClearSearch}>
    {t('Clear')}
  </Button>
</div>
        <Table
          columns={columns}
          dataSource={students} // filtered and paginated by the server
          rowKey="userid"
          pagination={{
            current: currentPage,
            pageSize: rowsPerPage,
            total: totalItems, // Use server's totalItems
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          scroll={{ x: 'max-content' }}
        />
      <PasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onConfirm={confirmDelete}
        errorMessage={passwordError}
      />
      <EditStudentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={saveStudent}
        studentId={studentIdToEdit}
      />

      <EditLinkedStudentForm
  visible={isLinkedStudentModalOpen}
  onClose={() => setIsLinkedStudentModalOpen(false)}
  mainUserId={studentIdToEdit} // Use the stored student ID
  onUpdated={refreshStudentData}
/>

      <StudentBookImagesModal
        visible={isBookImagesModalOpen}
        onClose={() => setIsBookImagesModalOpen(false)}
        studentId={bookImagesStudent?.userid}
        studentName={
          bookImagesStudent
            ? `${bookImagesStudent.firstname || ''} ${bookImagesStudent.lastname || ''}`.trim()
            : ''
        }
        onChange={refreshStudentData}
      />
    </div>
  );
}

export default withRoleAccess(EditDeleteStudent, ['admin', 'superadmin']);
